import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { NAV_LINKS, SITE_CONFIG } from '../../constants';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleDropdownToggle = (label: string) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || isMenuOpen
          ? 'bg-sand-50/95 backdrop-blur-md shadow-sm border-b border-sand-200'
          : 'bg-gradient-to-b from-earth-900/70 via-earth-900/40 to-transparent backdrop-blur-[2px]'
      }`}
    >
      <nav className="container-custom py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden bg-white shadow-sm">
              <img 
                src="/logo.crop.png" 
                alt="Beyond Class" 
                className="w-full h-full object-cover"
              />
            </div>
            <span className={`text-lg sm:text-xl font-semibold transition-colors ${
              scrolled || isMenuOpen ? 'text-earth-800' : 'text-white'
            }`}>
              {SITE_CONFIG.name}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <div key={link.path} className="relative group">
                {link.children ? (
                  <>
                    <button
                      onClick={() => handleDropdownToggle(link.label)}
                      className={`flex items-center gap-1.5 font-medium transition-colors ${
                        scrolled 
                          ? 'text-earth-700 hover:text-olive-600' 
                          : 'text-white/90 hover:text-white'
                      }`}
                    >
                      <span>{link.label}</span>
                      <i className={`ri-arrow-down-s-line transition-transform ${openDropdown === link.label ? 'rotate-180' : ''}`}></i>
                    </button>
                    <AnimatePresence>
                      {openDropdown === link.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-3 w-56 bg-white rounded-xl shadow-lg border border-sand-200 overflow-hidden"
                        >
                          {link.children.map((child) => (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              className={({ isActive }) =>
                                `block px-4 py-3 text-sm transition-colors hover:bg-sand-100 ${
                                  isActive ? 'text-olive-600 bg-sand-50' : 'text-earth-700'
                                }`
                              }
                            >
                              {child.label}
                            </NavLink>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <NavLink
                    to={link.path}
                    className={({ isActive }) =>
                      `font-medium transition-colors ${
                        scrolled
                          ? isActive ? 'text-olive-600' : 'text-earth-700 hover:text-olive-600'
                          : isActive ? 'text-white' : 'text-white/90 hover:text-white'
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link
              to="/contato"
              className={`inline-flex items-center gap-2 px-5 py-2.5 font-medium rounded-lg transition-all duration-200 ${
                scrolled 
                  ? 'bg-olive-600 text-white hover:bg-olive-700' 
                  : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 border border-white/20'
              }`}
            >
              Agende uma aula
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className={`lg:hidden p-2 transition-colors ${scrolled || isMenuOpen ? 'text-earth-700' : 'text-white'}`}
          >
            <i className={`text-2xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mt-4 pb-4 border-t border-sand-200 pt-4"
            >
              {NAV_LINKS.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {link.children ? (
                    <div className="py-2">
                      <button
                        onClick={() => handleDropdownToggle(link.label)}
                        className="flex items-center justify-between w-full text-earth-700 font-medium py-2"
                      >
                        <span>{link.label}</span>
                        <i className={`ri-arrow-down-s-line transition-transform ${openDropdown === link.label ? 'rotate-180' : ''}`}></i>
                      </button>
                      <AnimatePresence>
                        {openDropdown === link.label && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pl-4 mt-2 space-y-2 border-l-2 border-sand-200"
                          >
                            {link.children.map((child) => (
                              <NavLink
                                key={child.path}
                                to={child.path}
                                onClick={() => setIsMenuOpen(false)}
                                className={({ isActive }) =>
                                  `block py-2 text-sm ${isActive ? 'text-olive-600' : 'text-earth-600'}`
                                }
                              >
                                {child.label}
                              </NavLink>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <NavLink
                      to={link.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={({ isActive }) =>
                        `block py-3 font-medium ${isActive ? 'text-olive-600' : 'text-earth-700'}`
                      }
                    >
                      {link.label}
                    </NavLink>
                  )}
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-4"
              >
                <Link
                  to="/contato"
                  onClick={() => setIsMenuOpen(false)}
                  className="btn-primary w-full justify-center"
                >
                  Agende uma aula
                </Link>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
}
